import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { updateConsentData } from '../../../actions/refiApplicationActions'
import { selectConsentData } from '../../../reducers/refiApplication'
import { updateUser } from '../../../features/users/userActions'

interface ConsentFormProps {
  vehicle?: any
  user?: any
}

const parseBirthday = (birthday?: string | null) => {
  if (!birthday) return null
  const normalized = birthday.trim()
  const parts = normalized.includes('-') ? normalized.split('-') : normalized.split('/')
  if (parts.length !== 3) return null

  const year = parts[0]
  const month = parts[1]
  const day = parts[2]

  if (!year || !month || !day) return null

  return {
    dobYear: year,
    dobMonth: String(Number(month)),
    dobDay: String(Number(day)),
  }
}

const ConsentForm: React.FC<ConsentFormProps> = ({ user: userData }) => {
  const history = useHistory()
  const dispatch = useDispatch()
  const user = useSelector((state: any) => state.session?.user)
  const savedConsentData = useSelector(selectConsentData)

  const profileUser = userData || user
  const birthdayString = profileUser?.birthday || ''
  const birthdayFromProfile = parseBirthday(birthdayString)

  const [formData, setFormData] = useState({
    dobMonth: savedConsentData.dobMonth || birthdayFromProfile?.dobMonth || '',
    dobDay: savedConsentData.dobDay || birthdayFromProfile?.dobDay || '',
    dobYear: savedConsentData.dobYear || birthdayFromProfile?.dobYear || '',
    ssn: savedConsentData.ssn || '',
  })

  useEffect(() => {
    const hasSavedDOB = Boolean(savedConsentData.dobMonth || savedConsentData.dobDay || savedConsentData.dobYear)

    if (hasSavedDOB) {
      setFormData({
        dobMonth: savedConsentData.dobMonth || '',
        dobDay: savedConsentData.dobDay || '',
        dobYear: savedConsentData.dobYear || '',
        ssn: savedConsentData.ssn || '',
      })
      return
    }

    if (birthdayFromProfile) {
      setFormData(prev => ({
        ...prev,
        dobMonth: birthdayFromProfile.dobMonth,
        dobDay: birthdayFromProfile.dobDay,
        dobYear: birthdayFromProfile.dobYear,
      }))
    }
  }, [savedConsentData, birthdayString])

  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [touched, setTouched] = useState<{[key: string]: boolean}>({})

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target

    if (name === 'ssn') {
      const numericValue = value.replace(/\D/g, '')
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    if (touched[name]) {
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }))
      }
    }
  }

  // Generate options for dropdowns
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]

  const days = Array.from({ length: 31 }, (_, i) => i + 1)
  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i) // 18+ years old

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {}

    if (!formData.dobMonth) {
      newErrors.dobMonth = 'Month is required'
    }

    if (!formData.dobDay) {
      newErrors.dobDay = 'Day is required'
    }

    if (!formData.dobYear) {
      newErrors.dobYear = 'Year is required'
    }

    if (formData.dobMonth && formData.dobDay && formData.dobYear) {
      const dob = new Date(parseInt(formData.dobYear, 10), parseInt(formData.dobMonth, 10) - 1, parseInt(formData.dobDay, 10))
      const today = new Date()
      const age = today.getFullYear() - dob.getFullYear()
      const monthDiff = today.getMonth() - dob.getMonth()

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        if (age - 1 < 18) {
          newErrors.dobDate = 'You must be at least 18 years old'
        }
      } else if (age < 18) {
        newErrors.dobDate = 'You must be at least 18 years old'
      }
    }

    if (formData.ssn) {
      if (formData.ssn.length !== 9 || !/^\d{9}$/.test(formData.ssn)) {
        newErrors.ssn = 'SSN must be exactly 9 digits'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleApply = () => {
    setTouched({
      dobMonth: true,
      dobDay: true,
      dobYear: true,
      ssn: true
    })

    if (validateForm()) {
      dispatch(updateConsentData(formData))

      const profileUser = userData || user
      if (profileUser?.id && formData.dobMonth && formData.dobDay && formData.dobYear) {
        const normalizedDob = `${formData.dobYear}-${String(formData.dobMonth).padStart(2, '0')}-${String(formData.dobDay).padStart(2, '0')}`
        dispatch(updateUser({ id: profileUser.id, birthday: normalizedDob }))
      }

      history.push('/refiApp/financing/apply/disclosure-agreement')
    }
  }

  const handleBack = () => {
    history.push('/refiApp/financing/apply/income-details')
  }

  return (
    <div className="consent-form" style={{ maxWidth: '540px', margin: '0 auto', padding: '20px' }}>
      <div className="consent-form__content">
        <div className="consent-form__header">
          <h1 className="consent-form__title">{user?.firstName || 'Hi'}, One last step</h1>
          <p className="consent-form__description">
            Our lender partners need your consent to apply for financing. For the most accurate response its best to provided both date of birth and social security number.
          </p>
        </div>

        <div className="consent-form__fields">
          <div className="form__section">
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="dobMonth" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Month
                </label>
                <select
                  id="dobMonth"
                  name="dobMonth"
                  value={formData.dobMonth}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, dobMonth: true }))}
                  className={`form__input ${errors.dobMonth ? 'form__input--error' : ''}`}
                  style={{ width: '100%' }}
                >
                  <option value="">MM</option>
                  {months.map((month, index) => (
                    <option key={month} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
                {touched.dobMonth && errors.dobMonth && (
                  <p className="form__help-text text-error" style={{ fontSize: '12px', marginTop: '2px', marginBottom: '0' }}>{errors.dobMonth}</p>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="dobDay" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Day
                </label>
                <select
                  id="dobDay"
                  name="dobDay"
                  value={formData.dobDay}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, dobDay: true }))}
                  className={`form__input ${errors.dobDay ? 'form__input--error' : ''}`}
                  style={{ width: '100%' }}
                >
                  <option value="">DD</option>
                  {days.map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </select>
                {touched.dobDay && errors.dobDay && (
                  <p className="form__help-text text-error" style={{ fontSize: '12px', marginTop: '2px', marginBottom: '0' }}>{errors.dobDay}</p>
                )}
              </div>
              <div style={{ flex: 1 }}>
                <label htmlFor="dobYear" className="form__label" style={{ fontSize: '14px', marginBottom: '5px' }}>
                  Year
                </label>
                <select
                  id="dobYear"
                  name="dobYear"
                  value={formData.dobYear}
                  onChange={handleSelectChange}
                  onBlur={() => setTouched(prev => ({ ...prev, dobYear: true }))}
                  className={`form__input ${errors.dobYear ? 'form__input--error' : ''}`}
                  style={{ width: '100%' }}
                >
                  <option value="">YYYY</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                {touched.dobYear && errors.dobYear && (
                  <p className="form__help-text text-error" style={{ fontSize: '12px', marginTop: '2px', marginBottom: '0' }}>{errors.dobYear}</p>
                )}
              </div>
            </div>
            {touched.dobMonth && errors.dobDate && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.dobDate}</p>
            )}
          </div>

          <div className="form__section">
            <label htmlFor="ssn" className="form__label">
              SSN (optional)
            </label>
            <input
              id="ssn"
              name="ssn"
              type="text"
              value={formData.ssn}
              onChange={handleInputChange}
              onBlur={() => setTouched(prev => ({ ...prev, ssn: true }))}
              placeholder="123456789"
              className={`form__input form__input--full-width ${errors.ssn ? 'form__input--error' : ''}`}
              maxLength={9}
            />
            {touched.ssn && errors.ssn && (
              <p className="form__help-text text-error" style={{ marginTop: '2px', marginBottom: '0' }}>{errors.ssn}</p>
            )}
          </div>
        </div>

        <div className="consent-form__actions" style={{ marginTop: '30px' }}>
          <button
            type="button"
            onClick={handleApply}
            className="button button--primary button--full-width"
          >
            Next
          </button>
        </div>

        <div className="consent-form__navigation" style={{ marginTop: '20px' }}>
          <button
            type="button"
            onClick={handleBack}
            className="button button--link-text"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  )
}

ConsentForm.displayName = 'Consent Form'

export default ConsentForm
